/**
 * The formatting a message body may carry, and nothing more.
 *
 * Messages are stored as the text somebody typed — a small Markdown subset,
 * never HTML — and turned into structure only when read. This module is that
 * one reading: the web client renders the tree it returns as React elements,
 * and the server flattens the same tree into plain text for every place that
 * cannot show formatting (notifications, push, search snippets). One parser for
 * both means a preview can never disagree with the message about what was
 * markup and what was text.
 *
 * The subset: **bold** (or __bold__), *italic* (or _italic_), ~~strike~~,
 * `inline code`, ``` fenced code blocks ```, > quotes, - bulleted and 1.
 * numbered lists, [text](url) links, and bare http(s) URLs. Mentions in their
 * wire form `@[Name](id)` and plain `@handle` tokens (`@channel`, `@here`, a
 * user group) come out as their own nodes so nothing downstream has to re-find
 * them in the text.
 *
 * Deliberately not a general Markdown implementation: no headings, images,
 * tables or raw HTML. Anything this does not recognise stays literal text,
 * which is what a chat message that happens to contain a `*` should do.
 */
import { parseLinks } from "./links.js";
/** How deep quotes, lists and emphasis may nest. Beyond it, text stays text —
 *  enough for anything a person writes, and a ceiling on what a crafted body
 *  can make the renderer build. */
const MAX_DEPTH = 6;
/** Schemes a link may point at. Anything else — `javascript:`, `data:`,
 *  `vbscript:`, a relative path — is not a link. */
const SAFE_SCHEMES = new Set(["http:", "https:", "mailto:"]);
/**
 * Whether `href` may become a clickable link.
 *
 * Parsed with the URL constructor rather than matched with a prefix test, so
 * whatever a browser would treat as the scheme is what is checked: case,
 * leading whitespace and control characters cannot smuggle `javascript:` past
 * a string comparison. A relative or unparsable value is refused outright.
 */
export function isSafeHref(href) {
    if ([...href].some((ch) => ch.charCodeAt(0) <= 0x20))
        return false;
    try {
        return SAFE_SCHEMES.has(new URL(href).protocol);
    }
    catch {
        return false;
    }
}
/* ───────────────────────────────── inline ───────────────────────────────── */
const ESCAPABLE = new Set([..."\\`*_~[]()>#+-.!@|"]);
const MENTION_AT = /@\[([^\]\n]{1,120})\]\(([0-9a-fA-F-]{36})\)/y;
const HANDLE_AT = /@([A-Za-z0-9][A-Za-z0-9._-]{0,39})/y;
const LINK_AT = /\[([^\]\n]{1,1000})\]\(([^()\s]{1,2048})\)/y;
const URL_AT = /https?:\/\/[^\s<>"]+/iy;
/** Longest first, so `**` is tried before `*`. */
const DELIMITERS = [
    { marker: "**", type: "strong" },
    { marker: "__", type: "strong" },
    { marker: "~~", type: "strike" },
    { marker: "*", type: "em" },
    { marker: "_", type: "em" },
];
const isSpace = (ch) => ch === undefined || /\s/.test(ch);
const isWordChar = (ch) => ch !== undefined && /[\p{L}\p{N}]/u.test(ch);
/** Where a run of `n` backticks starting after `from` closes on this line, or -1. */
function findBacktickClose(src, from, n) {
    let j = from;
    while (j < src.length && src[j] !== "\n") {
        if (src[j] === "`") {
            let run = 0;
            while (src[j + run] === "`")
                run += 1;
            if (run === n)
                return j;
            j += run;
        }
        else
            j += 1;
    }
    return -1;
}
/**
 * Where the emphasis opened at `from` closes, or -1.
 *
 * On the same line only — emphasis that runs on for paragraphs is almost always
 * a stray `*`, not intent — and skipping code spans and escapes, so a `*`
 * inside `code` cannot close anything. A single-character marker never closes
 * against half of a doubled one (`*a **b** c*`).
 */
function findCloser(src, from, marker) {
    const single = marker.length === 1;
    let j = from;
    while (j < src.length && src[j] !== "\n") {
        const ch = src[j];
        if (ch === "\\") {
            j += 2;
            continue;
        }
        if (ch === "`") {
            let n = 0;
            while (src[j + n] === "`")
                n += 1;
            const close = findBacktickClose(src, j + n, n);
            j = close === -1 ? j + n : close + n;
            continue;
        }
        if (src.startsWith(marker, j) && j > from && !isSpace(src[j - 1])) {
            const after = src[j + marker.length];
            const doubled = single && (src[j - 1] === ch || after === ch);
            const intraword = marker[0] === "_" && isWordChar(after);
            if (!doubled && !intraword)
                return j;
        }
        j += single && src[j + 1] === ch ? 2 : 1;
    }
    return -1;
}
function parseInlines(src, depth, inLink) {
    const out = [];
    let text = "";
    const flush = () => {
        if (text)
            out.push({ type: "text", text });
        text = "";
    };
    const push = (node) => {
        flush();
        out.push(node);
    };
    // A marker already known to have no closer from some position on this line
    // cannot gain one later in it; remembering that keeps a line full of stray
    // `*` linear rather than quadratic.
    const noCloserFrom = new Map();
    let i = 0;
    while (i < src.length) {
        const ch = src[i];
        const prev = src[i - 1];
        if (ch === "\\" && i + 1 < src.length && ESCAPABLE.has(src[i + 1])) {
            text += src[i + 1];
            i += 2;
            continue;
        }
        if (ch === "\n") {
            push({ type: "break" });
            noCloserFrom.clear();
            i += 1;
            continue;
        }
        if (ch === "`") {
            let n = 0;
            while (src[i + n] === "`")
                n += 1;
            const close = findBacktickClose(src, i + n, n);
            if (close > i + n) {
                push({ type: "code", text: src.slice(i + n, close) });
                i = close + n;
            }
            else {
                text += src.slice(i, i + n);
                i += n;
            }
            continue;
        }
        if (ch === "@" && (prev === undefined || !/[\p{L}\p{N}_.+-]/u.test(prev))) {
            MENTION_AT.lastIndex = i;
            const mention = MENTION_AT.exec(src);
            if (mention) {
                push({ type: "mention", label: mention[1], userId: mention[2] });
                i += mention[0].length;
                continue;
            }
            HANDLE_AT.lastIndex = i;
            const handle = HANDLE_AT.exec(src);
            if (handle) {
                // A sentence ends a handle; "@here." means @here.
                const name = handle[1].replace(/[._-]+$/, "");
                push({ type: "handle", name });
                i += 1 + name.length;
                continue;
            }
        }
        if (ch === "[" && !inLink) {
            LINK_AT.lastIndex = i;
            const link = LINK_AT.exec(src);
            if (link && isSafeHref(link[2])) {
                push({
                    type: "link",
                    href: link[2],
                    children: depth < MAX_DEPTH ? parseInlines(link[1], depth + 1, true) : [{ type: "text", text: link[1] }],
                });
                i += link[0].length;
                continue;
            }
        }
        if ((ch === "h" || ch === "H") && !inLink && !isWordChar(prev)) {
            URL_AT.lastIndex = i;
            const candidate = URL_AT.exec(src);
            // parseLinks owns the trailing-punctuation rules for a bare URL, so the
            // rendered link and the one the old plain renderer found are the same.
            const found = candidate ? parseLinks(candidate[0])[0] : undefined;
            if (found && found.start === 0) {
                push({ type: "link", href: found.url, children: [{ type: "text", text: found.url }] });
                i += found.end;
                continue;
            }
        }
        const delimiter = DELIMITERS.find((d) => src.startsWith(d.marker, i));
        if (delimiter && depth < MAX_DEPTH) {
            const { marker } = delimiter;
            const opensWord = !isSpace(src[i + marker.length]);
            const leftOk = marker[0] !== "_" || !isWordChar(prev);
            const knownMissing = (noCloserFrom.get(marker) ?? Infinity) <= i;
            if (opensWord && leftOk && !knownMissing) {
                const close = findCloser(src, i + marker.length, marker);
                if (close !== -1) {
                    push({ type: delimiter.type, children: parseInlines(src.slice(i + marker.length, close), depth + 1, inLink) });
                    i = close + marker.length;
                    continue;
                }
                noCloserFrom.set(marker, i);
            }
            text += marker;
            i += marker.length;
            continue;
        }
        text += ch;
        i += 1;
    }
    flush();
    return out;
}
/* ───────────────────────────────── blocks ───────────────────────────────── */
const FENCE = /^ {0,3}```(.*)$/;
const QUOTE = /^ {0,3}>/;
const HEADING = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;
const TASK_ITEM = /^\[([ xX])\][ \t]+(.*)$/;
const LIST_ITEM = /^( *)([-*+•]|\d{1,9}[.)])[ \t]+(.*)$/;
function parseBlocks(lines, depth, rich) {
    const blocks = [];
    let paragraph = [];
    const flushParagraph = () => {
        while (paragraph.length && paragraph[paragraph.length - 1].trim() === "")
            paragraph.pop();
        if (paragraph.length)
            blocks.push({ type: "paragraph", children: parseInlines(paragraph.join("\n"), 0, false) });
        paragraph = [];
    };
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];
        const fence = FENCE.exec(line);
        if (fence) {
            flushParagraph();
            const rest = fence[1];
            // ```one line``` — the way people paste a single command.
            if (rest.length > 3 && rest.trimEnd().endsWith("```")) {
                blocks.push({ type: "codeBlock", text: rest.trimEnd().slice(0, -3), lang: null });
                i += 1;
                continue;
            }
            const content = [];
            let lang = null;
            if (/^[\w+#.-]{1,20}$/.test(rest.trim()))
                lang = rest.trim();
            else if (rest.trim())
                content.push(rest);
            i += 1;
            // Unclosed runs to the end of the message, as in every Markdown
            // implementation: the author is quoting code, and the code is the rest.
            while (i < lines.length) {
                const current = lines[i].trimEnd();
                i += 1;
                if (current.endsWith("```")) {
                    const before = current.slice(0, -3);
                    if (before.trim())
                        content.push(before);
                    break;
                }
                content.push(lines[i - 1]);
            }
            blocks.push({ type: "codeBlock", text: content.join("\n"), lang });
            continue;
        }
        const heading = rich ? HEADING.exec(line) : null;
        if (heading && heading[2].trim() !== "") {
            flushParagraph();
            blocks.push({
                type: "heading",
                level: heading[1].length,
                children: parseInlines(heading[2].trim(), 0, false),
            });
            i += 1;
            continue;
        }
        if (QUOTE.test(line) && depth < MAX_DEPTH) {
            flushParagraph();
            const quoted = [];
            while (i < lines.length && QUOTE.test(lines[i])) {
                quoted.push(lines[i].replace(/^ {0,3}> ?/, ""));
                i += 1;
            }
            blocks.push({ type: "quote", children: parseBlocks(quoted, depth + 1, rich) });
            continue;
        }
        const first = LIST_ITEM.exec(line);
        if (first && depth < MAX_DEPTH) {
            flushParagraph();
            const indent = first[1].length;
            const ordered = /\d/.test(first[2]);
            const items = [];
            while (i < lines.length) {
                const current = lines[i];
                const item = LIST_ITEM.exec(current);
                if (item && item[1].length <= indent + 1 && /\d/.test(item[2]) === ordered) {
                    items.push([item[3]]);
                    i += 1;
                    continue;
                }
                // Indented further than the marker: a nested list or a continuation
                // of the item above.
                const lead = /^ */.exec(current)[0].length;
                if (current.trim() !== "" && lead > indent && items.length) {
                    items[items.length - 1].push(current.slice(Math.min(lead, indent + 2)));
                    i += 1;
                    continue;
                }
                break;
            }
            const checks = items.map(() => null);
            if (rich && !ordered) {
                items.forEach((itemLines, n) => {
                    const task = TASK_ITEM.exec(itemLines[0]);
                    if (task) {
                        checks[n] = task[1] !== " ";
                        itemLines[0] = task[2];
                    }
                });
            }
            blocks.push({
                type: "list",
                ordered,
                start: ordered ? Math.min(Number.parseInt(first[2], 10), 1_000_000) : 1,
                items: items.map((itemLines) => parseBlocks(itemLines, depth + 1, rich)),
                ...(checks.some((c) => c !== null) ? { checks } : {}),
            });
            continue;
        }
        // Blank lines inside a paragraph are kept as line breaks rather than
        // splitting it: a chat message's spacing is the author's, and plain text
        // should look exactly as it did before formatting existed.
        if (line.trim() === "" && paragraph.length === 0) {
            i += 1;
            continue;
        }
        paragraph.push(line);
        i += 1;
    }
    flushParagraph();
    return blocks;
}
/** A message body as structure. Never throws; anything unrecognised is text. */
export function parseMarkdown(body, options = {}) {
    return parseBlocks(body.replace(/\r\n?/g, "\n").split("\n"), 0, options.rich === true);
}
/* ──────────────────────────────── plain text ─────────────────────────────── */
function inlineText(nodes) {
    let out = "";
    for (const node of nodes) {
        switch (node.type) {
            case "text":
            case "code":
                out += node.text;
                break;
            case "break":
                out += "\n";
                break;
            case "mention":
                out += `@${node.label}`;
                break;
            case "handle":
                out += `@${node.name}`;
                break;
            default:
                out += inlineText(node.children);
        }
    }
    return out;
}
function blockText(blocks) {
    return blocks
        .map((block) => {
        switch (block.type) {
            case "paragraph":
            case "heading":
                return inlineText(block.children);
            case "codeBlock":
                return block.text;
            case "quote":
                return blockText(block.children);
            case "list":
                return block.items
                    .map((item, index) => `${block.ordered ? `${block.start + index}.` : "•"} ${blockText(item)}`)
                    .join("\n");
        }
    })
        .join("\n");
}
/**
 * The body as a person reads it, with every piece of markup removed.
 *
 * For anything that shows a message as plain text — a notification, a push,
 * a search snippet, a quoted reply. Mentions come out as `@Name`, links as
 * their text, list items keep a bullet or number so they still read as a list.
 */
export function stripMarkdown(body, options = {}) {
    return blockText(parseMarkdown(body, options));
}
/**
 * The `@channel`, `@here` and `@handle` tokens a message actually uses.
 *
 * Read from the parsed tree rather than the text, so one inside code — a
 * person explaining how `@channel` works — notifies nobody.
 */
export function broadcastMentions(body) {
    const result = { channel: false, here: false, handles: [] };
    const handles = new Set();
    const visitInline = (nodes) => {
        for (const node of nodes) {
            if (node.type === "handle") {
                const name = node.name.toLowerCase();
                if (name === "channel" || name === "everyone")
                    result.channel = true;
                else if (name === "here")
                    result.here = true;
                else
                    handles.add(name);
            }
            else if (node.type === "strong" || node.type === "em" || node.type === "strike") {
                visitInline(node.children);
            }
        }
    };
    const visitBlocks = (blocks) => {
        for (const block of blocks) {
            if (block.type === "paragraph")
                visitInline(block.children);
            else if (block.type === "quote")
                visitBlocks(block.children);
            else if (block.type === "list")
                block.items.forEach(visitBlocks);
        }
    };
    visitBlocks(parseMarkdown(body));
    result.handles = [...handles];
    return result;
}
//# sourceMappingURL=markdown.js.map